import { defineStore } from 'pinia'
import { computed, ref, Ref } from 'vue'
import {
  helpers,
  maxValue,
  minLength,
  minValue,
  required,
  requiredIf,
  requiredUnless,
} from '@vuelidate/validators'

import { JobEndTimeframe, JobStartTimeframe, LocationPreference } from '@/gql/graphql'

import { $t } from '@/composables/translation'
import useApolloError from '@/composables/useApolloError'
import useCustomVuelidate from '@/composables/useCustomVuelidate'
import useApolloAndVuelidate from '@/composables/useApolloAndVuelidate'
import { useFieldableFormStore } from '@/components/Fieldable/store/useFieldableFormStore'

import { BUDGET_TYPE, JOB_TAB } from '@/components/Job/constants'
import { useJobFormStore } from '@/components/Job/Edit/store/jobFormStore'

const isPositiveAmount = (value: unknown): boolean => {
  if (value === undefined || value === null || value === '') {
    return false
  }
  const parsed = Number.parseFloat(String(value))
  return Number.isFinite(parsed) && parsed > 0
}

export const useValidationStore = defineStore('job-form-validation', () => {
  const jobFormStore = useJobFormStore()
  const fieldableFormStore = useFieldableFormStore()

  const errorUpdateJob: Ref = ref(undefined)
  const requireInternalBudget: Ref = ref(false)
  const requireJobRate = ref(false)

  const updateError = (error: any) => {
    errorUpdateJob.value = error
  }

  const overviewTabRules = {
    name: {
      required: helpers.withMessage($t('Name is required'), required),
      // minLength: helpers.withMessage($t('Name should be at least 5 characters long'), minLength(5)),
    },
    description: {
      required: helpers.withMessage($t('Description is required'), required),
      // minLength: helpers.withMessage(
      //   $t('Description should be at least 10 characters long'),
      //   minLength(10),
      // ),
    },
    expectedExperienceLevel: {
      required: helpers.withMessage($t('Seniority is required'), required),
    },
    skills: {
      required: helpers.withMessage($t('Skills are required'), required),
      minLength,
    },
    contactEmail: {
      email: helpers.withMessage($t('Please enter a valid email address'), (value: unknown) => {
        const trimmed = String(value ?? '').trim()
        return !trimmed || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)
      }),
    },
  }

  const detailsTabRules = {
    startDateTimeframe: {
      required: helpers.withMessage($t('Start date is required'), required),
    },
    startDate: {
      requiredIf: helpers.withMessage(
        $t('Specify a start date'),
        requiredIf(() => jobFormStore.form.startDateTimeframe === JobStartTimeframe.OnDate),
      ),
    },
    commitment: {
      required: helpers.withMessage($t('Commitment is required'), required),
    },
    evaluationPeriod: {
      required: helpers.withMessage($t('Length is required'), required),
    },
    endDate: {
      requiredIf: helpers.withMessage(
        $t('Specify a finish date'),
        requiredIf(() => jobFormStore.form.endDateTimeframe === JobEndTimeframe.OnDate),
      ),
    },
    locationPreference: { required },
    address: {
      requiredUnless: helpers.withMessage(
        $t('Location address is required'),
        requiredUnless(
          () => jobFormStore.form.locationPreference === LocationPreference.RemoteOnly,
        ),
      ),
    },
    postCode: {
      requiredUnless: helpers.withMessage(
        $t('Location post code is required'),
        requiredUnless(
          () => jobFormStore.form.locationPreference === LocationPreference.RemoteOnly,
        ),
      ),
    },
    city: {
      requiredUnless: helpers.withMessage(
        $t('Location city is required'),
        requiredUnless(
          () => jobFormStore.form.locationPreference === LocationPreference.RemoteOnly,
        ),
      ),
    },
    country: {
      requiredUnless: helpers.withMessage(
        $t('Location country is required'),
        requiredUnless(
          () => jobFormStore.form.locationPreference === LocationPreference.RemoteOnly,
        ),
      ),
    },
    state: {
      requiredIf: helpers.withMessage(
        $t('Location state is required'),
        requiredIf(() => ['AU', 'CA', 'US'].includes(jobFormStore.form.country ?? '')),
      ),
    },
  }

  const internalBudgetMinValue = computed(() => (requireInternalBudget.value === true ? 1 : 0))

  const budgetTabRules = {
    internalBudget: {
      minValue: helpers.withMessage(
        $t('Budget value is required'),
        minValue(internalBudgetMinValue),
      ),
    },
    rate: {
      requiredJobRate: helpers.withMessage($t('Rate is required'), (value: unknown) => {
        if (!requireJobRate.value) return true
        if (jobFormStore.form.budgetType !== BUDGET_TYPE.SPECIFIC_RATE) return true
        return isPositiveAmount(value)
      }),
    },
    minimumRate: {
      requiredIf: requiredIf(
        () =>
          jobFormStore.form.minimumRate === '0.00' ||
          jobFormStore.form.minimumRate === '0' ||
          jobFormStore.form.minimumRate !== undefined,
      ),
      requiredJobRate: helpers.withMessage($t('Minimum rate is required'), (value: unknown) => {
        if (!requireJobRate.value) return true
        if (jobFormStore.form.budgetType !== BUDGET_TYPE.RATE_RANGE) return true
        return isPositiveAmount(value)
      }),
    },
    maximumRate: {
      requiredIf: requiredIf(
        () =>
          jobFormStore.form.maximumRate === '0.00' ||
          jobFormStore.form.maximumRate === '0' ||
          jobFormStore.form.maximumRate !== undefined,
      ),
      requiredJobRate: helpers.withMessage($t('Maximum rate is required'), (value: unknown) => {
        if (!requireJobRate.value) return true
        if (jobFormStore.form.budgetType !== BUDGET_TYPE.RATE_RANGE) return true
        return isPositiveAmount(value)
      }),
    },
  }

  const settingsTabRules = {
    requiredWorkers: { required, minValue: minValue(1), maxValue: maxValue(99) },
  }

  const rules = {
    ...overviewTabRules,
    ...detailsTabRules,
    ...budgetTabRules,
    ...settingsTabRules,
    $validationGroups: {
      [JOB_TAB.OVERVIEW]: Object.keys(overviewTabRules),
      [JOB_TAB.DETAILS]: Object.keys(detailsTabRules),
      [JOB_TAB.BUDGET]: Object.keys(budgetTabRules),
    },
  }

  const $externalResults = ref({})

  const apollo = computed(() => useApolloError(errorUpdateJob.value))

  const jobStoreForm = computed(() => jobFormStore.form)

  const customVuelidate = useCustomVuelidate(rules, jobStoreForm as Ref, {
    $autoDirty: false,
    $externalResults,
  })

  // UseApolloAndVuelidate provides some data formatting to display server errors + validation errors
  // with server errors having higher precedence
  // Additionally, add in errors from custom fields here
  const apolloAndVuelidate = computed(() => {
    const jobFormErrors = useApolloAndVuelidate(apollo.value, customVuelidate, 'input')

    return { ...fieldableFormStore.errors, ...jobFormErrors }
  })

  const setRequireJobBudget = (isJobBudgetRequired: boolean) => {
    requireInternalBudget.value = isJobBudgetRequired
  }

  const setRequireJobRate = (isJobRateRequired: boolean) => {
    requireJobRate.value = isJobRateRequired
  }

  const errors = computed(() => {
    const errorMessages: Array<Ref<string>> = Object.values(apolloAndVuelidate.value)

    return errorMessages
      .filter((errorMessage) => errorMessage.value)
      .map((errorMessage) => errorMessage.value)
  })

  const hasErrors = computed(() => errors.value.length > 0)

  return {
    // State
    customVuelidate,
    apolloAndVuelidate,
    rules,
    errors,
    hasErrors,
    errorUpdateJob,
    requireJobRate,
    $externalResults,

    // Actions
    updateError,
    setRequireJobBudget,
    setRequireJobRate,
  }
})
